import accountantApi from "api/accountant";
import {IAccountant} from "api/types";
import ProfileImage from "components/ProfileImage";
import Tooltip from "components/Tooltip";
import {DefaultProfileSrc} from "assets/images";
import {LinkedInIcon, AlignableIcon} from "assets/icons";
import {MapPin, Clock, Monitor, ArrowRight, Plus} from "lucide-react";
import {FC, useState, useRef, useEffect} from "react";
import {useMutation} from "@tanstack/react-query";
import Certification from "./ProfileCard/Certification";
import ChangeableProfilePicture from "./ProfileCard/ChangeableProfilePicture.tsx";
import EditableLink from "./EditableLink";
import {capitalize, extractUrl} from "../utils";
import {ACCOUNTANT_BIO_TRUNCATE_LENGTH} from "../constants";

const editableProfileFields = [
  "name",
  "bio",
  "state",
  "city",
  "employmentType",
  "zip",
  "softwareExperience",
  "linkedIn",
  "alignable",
] as const;

interface Props {
  user: IAccountant;
  editable: boolean;
  updating: boolean;
  openingGym: boolean;
  moreInfoOpen: boolean;
  setMoreInfoOpen: (v: boolean) => void;
  missing: string[];
  showCopyMagic: boolean;
  onEdit: () => void;
  onEditPassword: () => void;
  onMagic: () => void;
  onCopyMagic: () => void;
  onChangeCertification: (name: string, updated: Record<string, string | {url: string; imageUrl?: string}>) => void;
  onUpdateProfileField: (field: (typeof editableProfileFields)[number]) => (value: string) => void;
}

const AddBadgeTile: FC<{
  onAdd: (name: string, updated: Record<string, string | {url: string; imageUrl?: string}>) => void;
  disabled?: boolean;
}> = ({onAdd, disabled}) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState<{name?: string; imageUrl?: string} | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewUrlRef = useRef("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const loadPreview = (value: string) => {
    if (!value || !value.includes("credly.com/badges/")) {
      setPreview(null);
      previewUrlRef.current = "";
      return;
    }
    if (previewUrlRef.current === value) return;
    previewUrlRef.current = value;
    setPreviewing(true);
    setPreview(null);
    accountantApi
      .loadCertificationData(value)
      .then((data) => {
        if (previewUrlRef.current === value) setPreview(data);
      })
      .finally(() => setPreviewing(false));
  };

  const {mutate: addMutate, isPending: isAdding} = useMutation({
    mutationFn: (cert: string | {url: string; imageUrl?: string}) =>
      accountantApi.updateCertification(preview!.name!, cert),
    onSuccess: (updated) => {
      onAdd(preview!.name!, updated);
      setUrl("");
      setPreview(null);
      previewUrlRef.current = "";
      setOpen(false);
    },
  });

  const onSave = () => {
    if (!preview?.name) return;
    const cert = preview.imageUrl ? {url: url.trim(), imageUrl: preview.imageUrl} : url.trim();
    addMutate(cert);
  };

  const onClose = () => {
    setOpen(false);
    setUrl("");
    setPreview(null);
    previewUrlRef.current = "";
  };

  return (
    <div className="relative" ref={ref}>
      <Tooltip title="Add New Badge" subtitle="Click to add your Credly link" position="top" withoutPointer>
        <button
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          className="w-14 h-14 rounded-xl flex items-center justify-center text-brand-muted hover:text-brand-navy transition-colors disabled:opacity-50"
          style={{backgroundColor: "#EDE9E3", border: "2px dashed #D1CFC9"}}
        >
          <Plus className="w-5 h-5" />
        </button>
      </Tooltip>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 z-50 bg-white border border-brand-border rounded-xl p-3 w-64"
          style={{boxShadow: "0 4px 20px rgba(0,0,0,0.1)"}}
        >
          <p className="text-xs font-semibold text-brand-navy mb-2">Add Badge</p>
          <input
            type="text"
            value={url}
            autoFocus
            onChange={(e) => {
              const val = e.target.value;
              setUrl(val);
              loadPreview(val);
            }}
            placeholder="https://www.credly.com/badges/…"
            className="w-full text-xs rounded-lg px-2.5 py-1.5 outline-none border mb-2"
            style={{backgroundColor: "#F7F5F2", borderColor: "#E8E4DF", color: "#282D4D"}}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
            }}
          />
          {previewing && <p className="text-xs text-brand-muted mb-2">Loading…</p>}
          {preview && (
            <div className="flex items-center gap-2 mb-2">
              {preview.imageUrl && (
                <img
                  src={preview.imageUrl}
                  alt={preview.name}
                  className="w-9 h-9 rounded-lg object-contain flex-shrink-0"
                />
              )}
              {preview.name && <span className="text-xs font-medium text-brand-navy truncate">{preview.name}</span>}
              {!preview.name && !preview.imageUrl && <span className="text-xs text-brand-muted">Badge not found</span>}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={!preview?.name || isAdding}
              className="flex-1 text-xs font-semibold py-1.5 rounded-lg text-white hover:opacity-90 transition-opacity disabled:opacity-40"
              style={{backgroundColor: "#3D52AD"}}
            >
              Add
            </button>
            <button
              onClick={onClose}
              className="flex-1 text-xs font-semibold py-1.5 rounded-lg border border-brand-border text-brand-muted hover:text-brand-navy transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileSection: FC<Props> = ({
  user,
  editable,
  updating,
  openingGym,
  moreInfoOpen,
  setMoreInfoOpen,
  missing,
  showCopyMagic,
  onEdit,
  onEditPassword,
  onMagic,
  onCopyMagic,
  onChangeCertification,
  onUpdateProfileField,
}) => {
  const [city, state] = user.location?.split(", ") || [];

  return (
    <>
      {/* Profile card */}
      <div className="rounded-2xl p-6 mb-5 border border-brand-border bg-white">
        <div className="flex items-start gap-5">
          <div className="flex-shrink-0">
            {editable ? (
              <ChangeableProfilePicture src={user.image} />
            ) : (
              <ProfileImage
                src={user.image || DefaultProfileSrc}
                size={72}
                className="w-18 h-18 rounded-full object-cover"
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="text-xl font-bold text-brand-navy">{user.name || "—"}</h2>
              {!user.enterprise && (
                <EditableLink
                  value={user.linkedIn || ""}
                  icon={<LinkedInIcon className="w-3.5 h-3.5" />}
                  label="LinkedIn"
                  linkStyle={{backgroundColor: "#0A66C2", color: "white"}}
                  editable={editable}
                  onSave={onUpdateProfileField("linkedIn")}
                  disabled={updating}
                />
              )}
              {!user.enterprise && (
                <EditableLink
                  value={user.alignable || ""}
                  icon={<AlignableIcon className="w-3.5 h-3.5" />}
                  label="Alignable"
                  linkStyle={{backgroundColor: "#E8401C", color: "white"}}
                  editable={editable}
                  onSave={onUpdateProfileField("alignable")}
                  disabled={updating}
                />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-3">
              {user.location && (
                <span className="flex items-center gap-1.5 text-sm text-brand-muted">
                  <MapPin className="w-3.5 h-3.5" />
                  {[capitalize(city), state?.toUpperCase()].filter(Boolean).join(", ")}
                </span>
              )}
              {user.employmentType && !user.enterprise && (
                <span className="flex items-center gap-1.5 text-sm text-brand-muted">
                  <Clock className="w-3.5 h-3.5" />
                  {user.employmentType}
                </span>
              )}
              {Object.keys(user.softwareExperience).length > 0 && (
                <span className="flex items-center gap-1.5 text-sm text-brand-muted">
                  <Monitor className="w-3.5 h-3.5" />
                  {Object.keys(user.softwareExperience).join(", ")}
                </span>
              )}
            </div>

            {user.bio && (
              <p className="text-sm text-brand-navy leading-relaxed">
                {moreInfoOpen
                  ? user.bio
                  : user.bio.slice(0, ACCOUNTANT_BIO_TRUNCATE_LENGTH) +
                    (user.bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH ? "…" : "")}
                {user.bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH && (
                  <button
                    onClick={() => setMoreInfoOpen(!moreInfoOpen)}
                    className="ml-1 text-sm font-medium text-brand-orange underline underline-offset-2 hover:opacity-70 transition-opacity"
                  >
                    {moreInfoOpen ? "Show less" : "Read more"}
                  </button>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Credentials & Badges */}
        <div className="mt-5 pt-5 border-t border-brand-border">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-3">
            Credentials &amp; Badges
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(user.certifications || {}).map(([name, val]) => (
              <Certification
                key={name}
                name={name}
                url={extractUrl(val)}
                imageUrl={typeof val === "object" ? val.imageUrl : undefined}
                editable={editable}
                onChange={(_, updated) => onChangeCertification(name, updated)}
                disabled={updating}
              />
            ))}
            {editable && <AddBadgeTile onAdd={onChangeCertification} disabled={updating} />}
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-5 pt-5 border-t border-brand-border flex flex-wrap items-center gap-3">
          {editable && (
            <Tooltip
              position="bottom-start"
              className="whitespace-pre-line leading-loose"
              text={missing.join("\n") || "Profile complete!"}
            >
              <button
                onClick={onEdit}
                disabled={updating}
                className="px-5 py-2.5 text-sm font-semibold border border-brand-border rounded-xl text-brand-navy hover:border-brand-navy transition-colors disabled:opacity-50"
              >
                Edit Profile
              </button>
            </Tooltip>
          )}

          {editable && (
            <button
              onClick={onEditPassword}
              className="px-5 py-2.5 text-sm font-semibold border border-brand-border rounded-xl text-brand-muted hover:text-brand-navy hover:border-brand-navy transition-colors"
            >
              Change Password
            </button>
          )}

          {showCopyMagic && (
            <button
              onClick={onCopyMagic}
              className="px-5 py-2.5 text-sm font-semibold border border-brand-border rounded-xl text-brand-muted hover:text-brand-navy transition-colors"
            >
              Copy Magic Link
            </button>
          )}

          <button
            disabled={openingGym}
            onClick={onMagic}
            className="ml-auto flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{backgroundColor: "#3D52AD"}}
          >
            <ArrowRight className="w-4 h-4" />
            Go to Dashboard
          </button>
        </div>
      </div>
    </>
  );
};

export default ProfileSection;
